// Order history straight from Kwikpass's own customer API.
//
// WHY THIS EXISTS
// Lumus runs New Customer Accounts, so a Kwikpass OTP login never produces a
// Shopify customer session and `customer.orders` in Liquid is always empty (see
// snippets/kwikpass-account-bridge.liquid). Kwikpass's own account page manages
// it because it doesn't use Shopify's session at all — it calls GoKwik with the
// token its login already put in the browser. This does the same thing, so the
// theme's order page works for a Kwikpass-logged-in customer with no second
// sign-in and no backend of our own.
//
// The contract below is GoKwik's, read off the same bundle their account page
// loads (pdp.gokwik.co/kp-account/mountApp.js): endpoints, header names, the
// token chain, and the response shape. It is not a documented public API, so
// every field is read defensively and any failure falls back to the sign-in CTA
// rather than breaking the page.
//
// Auth chain, in GoKwik's own order of preference:
//   ELRTOKEN            → use as-is
//   KWIKUSERTOKEN       → use as-is
//   KWIKSESSIONTOKEN    → exchange at /auth/core-token/login for a user token
// Each is looked up in cookies first, then localStorage.

const API_BASE = 'https://gkx.gokwik.co';

const ENDPOINTS = {
  ORDERS: '/kp/api/v1/customer/orders',
  ORDER_DETAILS: '/kp/api/v1/customer/order',
  CORE_TOKEN_LOGIN: '/kp/api/v1/auth/core-token/login',
};

const TOKEN_KEYS = {
  DIRECT: 'ELRTOKEN',
  USER: 'KWIKUSERTOKEN',
  SESSION: 'KWIKSESSIONTOKEN',
  REQUEST_ID: 'kp-request-id',
  // Cookie name, which is NOT the header name: GoKwik stores the Shopify
  // customer access token in `acs_tkn` and sends it as the header below.
  // Looking for a cookie named after the header finds nothing, the header is
  // omitted, and their API answers 401 "undefined is missing".
  CUSTOMER_ACCESS_COOKIE: 'acs_tkn',
  CUSTOMER_ACCESS_HEADER: 'X-Shopify-Customer-Access-Token',
};

const DEFAULT_REQUEST_ID = 'kwikPass_plugin';
const ORDERS_PAGE_SIZE = 20;

// GoKwik returns these already-humanised strings in `status`.
const STATUS_GROUPS = {
  'in processing': 'processing',
  shipped: 'shipped',
  canceled: 'cancelled',
  cancelled: 'cancelled',
  returned: 'cancelled',
};

/* -------------------------------------------------------------------------- *
 * Storage helpers
 * -------------------------------------------------------------------------- */

function readCookie(name) {
  const entries = (document.cookie || '').split(';');
  for (const entry of entries) {
    const [key, ...rest] = entry.trim().split('=');
    if (key === name) return rest.join('=');
  }
  return null;
}

function readStored(name) {
  const fromCookie = readCookie(name);
  if (fromCookie) return fromCookie;
  try {
    return localStorage.getItem(name);
  } catch (error) {
    return null;
  }
}

// Failures here are invisible otherwise — the page just shows its sign-in CTA —
// so every give-up path logs why, and ?kpdebug=1 turns on the full trace.
const DEBUG = /[?&]kpdebug/.test(window.location.search);

function log(...args) {
  if (DEBUG) console.log('[kp-orders]', ...args);
}

function warn(...args) {
  console.warn('[kp-orders]', ...args);
}

// Lists the Kwikpass-looking keys actually present, so a renamed token key
// shows up immediately instead of looking like "logged out".
function storageSnapshot() {
  const cookies = (document.cookie || '')
    .split(';')
    .map((entry) => entry.trim().split('=')[0])
    .filter(Boolean);

  let localKeys = [];
  try {
    localKeys = Object.keys(localStorage);
  } catch (error) {
    localKeys = ['<localStorage unavailable>'];
  }

  const interesting = (key) => /gokwik|kwik|^gk|^kp|elr|token/i.test(key);

  return {
    merchantInfo: window.merchantInfo || null,
    cookieKeys: cookies.filter(interesting),
    localStorageKeys: localKeys.filter(interesting),
    resolved: {
      ELRTOKEN: Boolean(readStored(TOKEN_KEYS.DIRECT)),
      KWIKUSERTOKEN: Boolean(readStored(TOKEN_KEYS.USER)),
      KWIKSESSIONTOKEN: Boolean(readStored(TOKEN_KEYS.SESSION)),
      // Cookies Kwikpass writes on a successful login (kp-merchant-v2.js):
      // acs_tkn = Shopify customer access token, sc_tkn = Shopify customer id,
      // cap_tkn = custom-account-page token.
      acs_tkn: Boolean(readStored(TOKEN_KEYS.CUSTOMER_ACCESS_COOKIE)),
      sc_tkn: Boolean(readStored('sc_tkn')),
      cap_tkn: Boolean(readStored('cap_tkn')),
      kp_customer_id: window.kp_customer_id || null,
      requestId: requestId(),
    },
  };
}

function merchantId() {
  return window.merchantInfo?.mid || '';
}

function requestId() {
  return readStored(TOKEN_KEYS.REQUEST_ID) || DEFAULT_REQUEST_ID;
}

/* -------------------------------------------------------------------------- *
 * API client
 * -------------------------------------------------------------------------- */

function baseHeaders() {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    // GoKwik's own client sends the merchant id as gk-merchant-id on customer
    // calls and as both gk-/kp- on the token exchange. Sending both everywhere
    // is harmless and covers whichever one the endpoint validates.
    'gk-merchant-id': merchantId(),
    'kp-merchant-id': merchantId(),
    'kp-integration-type': 'PLUGIN',
    'gk-platform': 'shopify',
  };
}

async function apiGet(path, headers) {
  log('GET', path, headers);
  const response = await fetch(API_BASE + path, { method: 'GET', headers });

  if (!response.ok) {
    let body = '';
    try {
      body = (await response.text()).slice(0, 500);
    } catch (readError) {
      /* body already consumed or unreadable */
    }
    warn(`GET ${path} → HTTP ${response.status}`, body);
    const error = new Error(`GoKwik ${path} responded ${response.status}`);
    error.status = response.status;
    throw error;
  }

  const payload = await response.json();
  log('response', path, payload);
  return payload;
}

// Trades the login-time session token for the user token the customer
// endpoints expect, and caches it the way GoKwik's own client does.
async function exchangeSessionToken(sessionToken) {
  const payload = await apiGet(ENDPOINTS.CORE_TOKEN_LOGIN, {
    ...baseHeaders(),
    'kp-merchant-id': merchantId(),
    'kp-request-id': requestId(),
    token: sessionToken,
  });

  const token = payload?.data?.token;
  if (!token) return null;

  try {
    localStorage.setItem(TOKEN_KEYS.USER, token);
  } catch (error) {
    /* storage unavailable — the token still works for this page view */
  }
  return token;
}

// Set to the storage key the current token came from, for diagnostics.
let tokenSource = null;

// A freshly minted token takes precedence over anything in storage — see
// fetchOrdersWithRetry below.
let forcedToken = null;

async function accessToken() {
  if (forcedToken) {
    tokenSource = 'KWIKSESSIONTOKEN (re-exchanged)';
    return forcedToken;
  }

  const direct = readStored(TOKEN_KEYS.DIRECT);
  if (direct) {
    tokenSource = TOKEN_KEYS.DIRECT;
    return direct;
  }

  const user = readStored(TOKEN_KEYS.USER);
  if (user) {
    tokenSource = TOKEN_KEYS.USER;
    return user;
  }

  const session = readStored(TOKEN_KEYS.SESSION);
  if (!session) {
    warn('no Kwikpass token found — checked ELRTOKEN / KWIKUSERTOKEN / KWIKSESSIONTOKEN in cookies and localStorage', storageSnapshot());
    return null;
  }

  log('exchanging KWIKSESSIONTOKEN for a user token');
  tokenSource = TOKEN_KEYS.SESSION + ' (exchanged)';
  return exchangeSessionToken(session);
}

async function authHeaders() {
  const token = await accessToken();
  if (!token) return null;

  const headers = {
    ...baseHeaders(),
    'gk-access-token': token,
    'kp-request-id': requestId(),
  };

  const customerAccess = readStored(TOKEN_KEYS.CUSTOMER_ACCESS_COOKIE);
  if (customerAccess) headers[TOKEN_KEYS.CUSTOMER_ACCESS_HEADER] = customerAccess;

  return headers;
}

// Runs the orders request once with an explicit token, reporting the raw
// outcome. Used by debug() to find which token/header set GoKwik accepts.
async function probeWithToken(label, token) {
  if (!token) return { label, skipped: 'no token' };

  const headers = {
    ...baseHeaders(),
    'gk-access-token': token,
    'kp-request-id': requestId(),
    first: '5',
  };

  const customerAccess = readStored(TOKEN_KEYS.CUSTOMER_ACCESS_COOKIE);
  if (customerAccess) headers[TOKEN_KEYS.CUSTOMER_ACCESS_HEADER] = customerAccess;

  try {
    const response = await fetch(API_BASE + ENDPOINTS.ORDERS, { method: 'GET', headers });
    const body = (await response.text()).slice(0, 300);
    return { label, status: response.status, body };
  } catch (error) {
    return { label, error: String(error) };
  }
}

// GoKwik passes pagination as headers rather than query params — matching their
// client exactly, since the endpoint ignores query params for these.
async function fetchOrders({ first = ORDERS_PAGE_SIZE, after = null } = {}) {
  const headers = await authHeaders();
  if (!headers) return null;

  headers.first = String(first);
  if (after) headers.after = after;

  const payload = await apiGet(ENDPOINTS.ORDERS, headers);
  return payload?.data ?? null;
}

// GoKwik answers 404 "Customer not found" when it can't map the token to a
// Shopify customer — which a stale KWIKUSERTOKEN would also cause. Their login
// mints that token by exchanging KWIKSESSIONTOKEN, so do the same once and
// retry before giving up.
async function fetchOrdersWithRetry(options) {
  try {
    return await fetchOrders(options);
  } catch (error) {
    const retriable = error.status === 401 || error.status === 404;
    const session = readStored(TOKEN_KEYS.SESSION);
    if (!retriable || !session || forcedToken) throw error;

    log('retrying with a freshly exchanged token');
    const fresh = await exchangeSessionToken(session);
    if (!fresh) throw error;

    forcedToken = fresh;
    return fetchOrders(options);
  }
}

async function fetchOrderDetails(orderId) {
  const headers = await authHeaders();
  if (!headers) return null;

  const query = `?orderId=${encodeURIComponent(orderId)}`;
  const payload = await apiGet(ENDPOINTS.ORDER_DETAILS + query, headers);
  return payload?.data ?? null;
}

/* -------------------------------------------------------------------------- *
 * Normalising
 * -------------------------------------------------------------------------- */

function lineItemsOf(node) {
  return (node?.lineItems?.edges || []).map(({ node: item }) => {
    const product = item?.variant?.product;
    return {
      title: product?.title || item?.title || '',
      variantTitle: item?.variant?.title || '',
      quantity: item?.quantity || 1,
      imageUrl: item?.variant?.image?.url || product?.images?.edges?.[0]?.node?.url || '',
      amount: Number.parseFloat(item?.originalTotalPrice?.amount || '0') || 0,
      currency: item?.originalTotalPrice?.currencyCode || 'INR',
    };
  });
}

// Mirrors the Active/Completed/Cancelled/Refunded buckets
// sections/order-history.liquid computes from real Shopify order/fulfillment
// fields, so the tabs group orders the same way regardless of which path
// rendered them. GoKwik's `status` string already distinguishes "canceled"
// from "returned" (see STATUS_GROUPS above), so split on that directly
// rather than the collapsed statusGroup — that's the only real signal this
// API gives us for telling cancelled and refunded orders apart.
function statusBucketOf(status, fulfillmentStatus) {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'canceled' || normalized === 'cancelled') return 'cancelled';
  if (normalized === 'returned') return 'refunded';
  if ((fulfillmentStatus || '').toUpperCase() === 'FULFILLED') return 'completed';
  return 'active';
}

function normalizeOrders(data) {
  const edges = data?.orders?.edges || [];

  return edges
    .map(({ node }) => {
      if (!node) return null;
      const items = lineItemsOf(node);
      const status = node.status || '';
      const statusGroup = STATUS_GROUPS[status.toLowerCase()] || 'processing';
      const fulfillmentStatus = node.fulfillmentStatus || '';

      return {
        // `id` is the Shopify GID the details endpoint wants; `name` is "#1234".
        id: node.id,
        name: node.name || '',
        status,
        statusGroup,
        statusBucket: statusBucketOf(status, fulfillmentStatus),
        fulfillmentStatus,
        items,
        total: items.reduce((sum, item) => sum + item.amount, 0),
        currency: items[0]?.currency || 'INR',
      };
    })
    .filter(Boolean);
}

/* -------------------------------------------------------------------------- *
 * Rendering — mirrors the markup sections/order-history.liquid renders from
 * Liquid, so the section's CSS and assets/order-history.js apply unchanged.
 * -------------------------------------------------------------------------- */

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (character) => {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
  });
}

function formatMoney(amount, currency) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
  } catch (error) {
    return `${currency} ${Math.round(amount)}`;
  }
}

// Mirrors the `remove: '+91' | remove: ' ' | remove: '-'` cleanup
// sections/order-history.liquid does before building the same link.
function cleanPhone(phone) {
  return String(phone || '').replace(/\+91/g, '').replace(/[\s-]/g, '');
}

// order.id here is the GID GoKwik's own API wants (gid://shopify/Order/123),
// but third-party app snippets like Codify's cancel button are written
// against native Liquid's order.id, which is always the plain numeric ID —
// so their script needs that, not the GID.
function numericOrderId(gid) {
  return String(gid || '').split('/').pop();
}

function cardMarkup(order) {
  const [first, ...rest] = order.items;
  const extra = rest.length;
  const meta = first?.variantTitle && first.variantTitle !== 'Default Title'
    ? `${escapeHtml(first.variantTitle)} · Qty ${first.quantity}`
    : `Qty ${first?.quantity ?? 1}`;
  const searchTerms = escapeHtml(
    [order.name, ...order.items.map((item) => item.title)].join(' ').toLowerCase()
  );

  return `
    <div class="order-history__card" data-order-card data-order-id="${escapeHtml(order.id)}" data-order-status-group="${order.statusBucket}" data-order-search="${searchTerms}">
      <div class="order-history__card-top">
        <div class="order-history__card-meta">
          <span class="order-history__order-name">${escapeHtml(order.name)}</span>
        </div>
        <div class="order-history__pill" data-status="${order.statusGroup}">
          <span class="order-history__pill-dot"></span>${escapeHtml(order.status || 'Processing')}
        </div>
      </div>

      <div class="order-history__card-body">
        ${first?.imageUrl
          ? `<div class="order-history__thumb"><img src="${escapeHtml(first.imageUrl)}" loading="lazy" width="68" height="68" alt="${escapeHtml(first.title)}"></div>`
          : ''}
        <div class="order-history__card-info">
          <div class="order-history__item-name">
            ${escapeHtml(first?.title || '')}
            ${extra ? `<span class="order-history__item-more">+${extra} more</span>` : ''}
          </div>
          <div class="order-history__item-meta">${meta}</div>
          <div class="order-history__price-row">
            <span class="order-history__price">${escapeHtml(formatMoney(order.total, order.currency))}</span>
          </div>
        </div>
      </div>

      <div class="order-history__actions">
        <button
          type="button"
          class="order-history__btn order-history__btn--ghost order-history__btn--track"
          data-kp-track
          data-order-id="${escapeHtml(order.id)}"
          data-order-name="${escapeHtml(order.name)}"
        >
          Track order
        </button>
        ${order.statusGroup === 'processing' ? `
        <button
          type="button"
          class="coc-button coc-cancel-button order-history__btn order-history__btn--ghost"
          data-order-id="${escapeHtml(numericOrderId(order.id))}"
          disabled
        >
          Cancel
        </button>
        ` : ''}
        <button type="button" class="order-history__btn order-history__btn--ghost order-history__btn--details" data-order-details-toggle aria-expanded="false">
          <span data-order-details-toggle-label>Show details</span>
          <svg class="order-history__chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"></path></svg>
        </button>
      </div>

      <div class="order-history__inline-details" data-order-inline-details hidden>
        <div class="order-history__details-grid">
          <div class="order-history__details-col">
            <div class="order-history__details-heading">Order details</div>
            <div class="order-history__details-row"><span>Order ID</span><span>${escapeHtml(order.name)}</span></div>
            <div class="order-history__details-row"><span>Product</span><span>${escapeHtml(order.items.map((item) => `${item.title} × ${item.quantity}`).join(', '))}</span></div>
            <div class="order-history__details-row"><span>Amount paid</span><span>${escapeHtml(formatMoney(order.total, order.currency))}</span></div>
            <div class="order-history__details-row"><span>Delivery</span><span>${escapeHtml(order.status || 'Processing')}</span></div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function appMarkup(orders) {
  const counts = orders.length === 1 ? '1 order' : `${orders.length} orders`;

  return `
    <order-history-app class="order-history__app">
      <div class="order-history__head">
        <h1 class="order-history__title">My Orders</h1>
        <p class="order-history__subtitle">${counts}</p>
      </div>

      <div class="order-history__toolbar">
        <div class="order-history__tabs" role="tablist" data-order-tabs>
          <button type="button" class="order-history__tab is-active" data-order-filter="all" aria-pressed="true">
            All <span class="order-history__tab-count">(${orders.length})</span>
          </button>
          <button type="button" class="order-history__tab" data-order-filter="active" aria-pressed="false">Active</button>
          <button type="button" class="order-history__tab" data-order-filter="completed" aria-pressed="false">Completed</button>
          <button type="button" class="order-history__tab" data-order-filter="cancelled" aria-pressed="false">Cancelled</button>
          <button type="button" class="order-history__tab" data-order-filter="refunded" aria-pressed="false">Refunded</button>
        </div>

        <div class="order-history__search">
          <span class="svg-wrapper order-history__search-icon">
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><circle cx="9" cy="9" r="4.75" stroke="currentColor" stroke-width="1.5"/><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m12.5 12.5 3.25 3.25"/></svg>
          </span>
          <input
            type="search"
            class="order-history__search-input"
            data-order-search-input
            placeholder="Search by order number or product"
            aria-label="Search orders"
          >
        </div>
      </div>

      <div class="order-history__layout" data-order-layout>
        <div class="order-history__list" data-order-list>
          ${orders.map(cardMarkup).join('')}
          <div class="order-history__empty-search" data-order-empty hidden>
            <p class="order-history__empty-search-text">No orders match your search.</p>
          </div>
        </div>
      </div>
    </order-history-app>
  `;
}

// The orders LIST endpoint carries no phone field at all (confirmed live —
// /pages/track-order was landing with the order number pre-filled and the
// phone blank). The per-order DETAILS endpoint is a richer payload, so this
// is fetched fresh on click and checked in every plausible spot; ?kpdebug=1
// logs the raw response so a still-missing phone can be tracked down by field
// name instead of another guess. Either way this always navigates — a failed
// or empty lookup just falls back to the order-only link track-order.js
// already knows how to pre-fill without auto-submitting.
async function goToTrackOrder(button) {
  if (button.dataset.navigating) return;
  button.dataset.navigating = 'true';

  const orderName = button.dataset.orderName || '';
  const fallbackHref = `/pages/track-order?order=${encodeURIComponent(orderName)}`;
  const originalLabel = button.textContent;
  button.textContent = 'Loading…';

  try {
    const details = await fetchOrderDetails(button.dataset.orderId);
    log('order details for phone lookup', details);

    const phone = cleanPhone(
      details?.phone
        || details?.customer?.phone
        || details?.shippingAddress?.phone
        || details?.billingAddress?.phone
        || ''
    );

    window.location.href = phone ? `${fallbackHref}&phone=${encodeURIComponent(phone)}` : fallbackHref;
  } catch (error) {
    warn('order-details lookup for tracking failed, falling back to order-only link', error);
    window.location.href = fallbackHref;
  } finally {
    // Only reached if navigation didn't actually happen (e.g. blocked popup).
    delete button.dataset.navigating;
    button.textContent = originalLabel;
  }
}

/* -------------------------------------------------------------------------- *
 * Entry point
 * -------------------------------------------------------------------------- */

function emit(mount, state, detail = {}) {
  mount.dispatchEvent(new CustomEvent(`kp-orders:${state}`, { bubbles: true, detail }));
}

// Compact "why did this fail" line, shown on the page while this is still a
// draft theme — screenshots are the fastest feedback loop we have. Remove the
// diagnostic markup in sections/order-history.liquid before publishing.
function diagnosticLine(status) {
  const snapshot = storageSnapshot();
  const yesNo = (value) => (value ? 'yes' : 'no');

  return [
    status ? `orders API ${status}` : 'no request made',
    `token: ${tokenSource || 'none'}`,
    `acs_tkn: ${yesNo(snapshot.resolved.acs_tkn)}`,
    `sc_tkn: ${yesNo(snapshot.resolved.sc_tkn)}`,
    `cap_tkn: ${yesNo(snapshot.resolved.cap_tkn)}`,
    `ELR: ${yesNo(snapshot.resolved.ELRTOKEN)}`,
    `KWIKUSER: ${yesNo(snapshot.resolved.KWIKUSERTOKEN)}`,
    `KWIKSESSION: ${yesNo(snapshot.resolved.KWIKSESSIONTOKEN)}`,
    `mid: ${snapshot.merchantInfo && snapshot.merchantInfo.mid ? snapshot.merchantInfo.mid : 'none'}`,
  ].join(' · ');
}

async function init() {
  const mount = document.querySelector('[data-kp-orders-mount]');
  if (!mount) {
    log('no [data-kp-orders-mount] on this page — nothing to do');
    return;
  }

  log('starting', storageSnapshot());

  try {
    const data = await fetchOrdersWithRetry();

    // No Kwikpass token at all, or GoKwik rejected it — the visitor has to
    // sign in, which is the section's fallback.
    if (!data) {
      warn('no usable Kwikpass session — showing the sign-in CTA');
      emit(mount, 'unauthenticated', { diagnostic: diagnosticLine(null) });
      return;
    }

    const orders = normalizeOrders(data);
    if (!orders.length) {
      log('API answered with zero orders', data);
      emit(mount, 'empty');
      return;
    }

    mount.innerHTML = appMarkup(orders);
    mount.hidden = false;

    // init() can run twice (page load, then again after a Kwikpass login), so
    // bind the delegated handler only once.
    if (!mount.dataset.kpBound) {
      mount.dataset.kpBound = 'true';
      mount.addEventListener('click', (event) => {
        const button = event.target.closest('[data-kp-track]');
        if (button) goToTrackOrder(button);
      });
    }

    emit(mount, 'rendered', { count: orders.length });
  } catch (error) {
    warn('failed to load orders', error);
    emit(mount, error.status === 401 || error.status === 404 ? 'unauthenticated' : 'error', {
      error,
      diagnostic: diagnosticLine(error.status),
    });
  }
}

// Console entry point: `LumusKwikpassOrders.debug()` prints the token/merchant
// state and runs a live orders request, returning whatever comes back.
window.LumusKwikpassOrders = {
  debug: async () => {
    const snapshot = storageSnapshot();
    console.log('[kp-orders] state\n' + JSON.stringify(snapshot, null, 2));

    const token = await accessToken();
    console.log('[kp-orders] token source:', tokenSource, token ? `${String(token).slice(0, 16)}…` : '(none)');

    // Which token does GoKwik actually accept? Try each candidate on its own.
    const results = [];
    results.push(await probeWithToken('ELRTOKEN', readStored(TOKEN_KEYS.DIRECT)));
    results.push(await probeWithToken('KWIKUSERTOKEN', readStored(TOKEN_KEYS.USER)));

    const session = readStored(TOKEN_KEYS.SESSION);
    if (session) {
      results.push(await probeWithToken('KWIKSESSIONTOKEN (raw)', session));
      try {
        const exchanged = await exchangeSessionToken(session);
        results.push(await probeWithToken('KWIKSESSIONTOKEN (exchanged)', exchanged));
      } catch (error) {
        results.push({ label: 'KWIKSESSIONTOKEN (exchange)', error: String(error) });
      }
    }

    console.table(results);
    return { snapshot, tokenSource, results };
  },
  fetchOrders,
  fetchOrderDetails,
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Kwikpass fires this the moment an OTP login succeeds. The token it just wrote
// is what the API needs, so re-run rather than leaving the customer looking at
// the sign-in CTA they only just satisfied.
window.addEventListener('user-loggedin', () => {
  log('user-loggedin — retrying orders');
  init();
});
